/**
 * Styles residuais do CRM — apenas o que NAO tem classe canonica equivalente.
 *
 * O grosso da tela usa classes de src/styles/canonical/comercial.css
 * (.kanban / .kanban__card / .pipe-cfg-row / ...) e components.css
 * (.btn / .badge / .input-prefixed / .tabchip / ...). Aqui ficam so os
 * residuos: dropdown ancorado (ExportMenu / acoes de coluna), blocos do
 * detalhe do lead e a timeline de eventos.
 */
import styled from "styled-components";

import { getToneTokens } from "../../styles/shared";

// ---------- Dropdown ancorado (ExportMenu + acoes de coluna) ----------

export const MenuWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

export const MenuList = styled.ul`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 180px;
  z-index: 30;

  /* Auxiliares do menu de coluna (secao de periodo de entrada na etapa). */
  .menu-sep {
    height: 1px;
    margin: 6px 4px;
    background: ${({ theme }) => theme.border};
  }
  .menu-titulo {
    padding: 4px 10px 2px;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.6;
  }
  /* Linha De/Até do intervalo especifico. */
  .menu-datas {
    display: flex;
    gap: 8px;
    padding: 4px 10px 8px;
  }
  .menu-datas label {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 0.72rem;
    opacity: 0.75;
    flex: 1;
  }
  .menu-datas input {
    width: 100%;
    padding: 4px 6px;
    font-size: 0.8rem;
    color: ${({ theme }) => theme.text};
    background: ${({ theme }) => theme.surfaceAlt};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 6px;
  }
  .menu-nota {
    padding: 2px 10px 6px;
    font-size: 0.72rem;
    opacity: 0.55;
  }
`;

export const MenuItem = styled.li`
  > button {
    width: 100%;
    text-align: left;
    background: transparent;
    border: 0;
    padding: 7px 10px;
    border-radius: 6px;
    font-size: 0.85rem;
    cursor: pointer;
    color: ${({ theme, $danger }) =>
      $danger ? theme.dangerText : theme.textPrimary};
    display: flex;
    align-items: center;
    gap: 8px;
  }
  > button:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }
  > button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ---------- Detalhe do lead (dentro do SlideOverPanel) ----------

export const DetalheStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 8px;
  font-size: 0.88rem;
  align-items: baseline;

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
    gap: 2px;
  }
`;

export const FieldLabel = styled.span`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const FieldValue = styled.span`
  color: ${({ theme }) => theme.textPrimary};
  overflow-wrap: anywhere;
`;

export const SectionTitle = styled.h4`
  margin: 0;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.textSecondary};
`;

// ---------- Timeline de eventos do lead ----------

export const EventList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const EventItem = styled.li`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  padding: 10px 0;
  border-bottom: 1px dashed ${({ theme }) => theme.border};
  font-size: 0.85rem;

  &:last-child {
    border-bottom: 0;
  }
`;

export const EventDate = styled.span`
  font-family: var(--font-mono);
  font-size: 0.72rem;
  color: ${({ theme }) => theme.textSecondary};
  white-space: nowrap;
`;

// ---------- Pill de tom (reaproveita tokens funcionais) ----------

export const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${({ theme, $tone = "neutral" }) =>
    getToneTokens(theme, $tone).background};
  color: ${({ theme, $tone = "neutral" }) => getToneTokens(theme, $tone).text};
  border: 1px solid
    ${({ theme, $tone = "neutral" }) => getToneTokens(theme, $tone).border};
  white-space: nowrap;
`;

// Fieldset de form generico (reaproveitado pelos forms/dialogs do CRM).
export const FormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.textSecondary};
`;

export const Input = styled.input`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.88rem;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -1px;
    border-color: transparent;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const Select = styled.select`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.85rem;
  cursor: pointer;
`;

export const Textarea = styled.textarea`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.88rem;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
`;

export const InlineHint = styled.span`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.textTertiary};
`;

export const InlineError = styled.span`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.dangerText};
`;
